'use client'

import { useState, useEffect, useRef } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { Search, X, Clock } from 'lucide-react'
import HSChapterFilter from '../HSChapterFilter'
import HSTaxFilter from '../HSTaxFilter'
import clsx from 'clsx'
import { useSearchHistory } from '@/modules/shared/hooks/useSearchHistory'

const TAX_LABELS: Record<string, string> = {
  TTDB: 'Thuế tiêu thụ đặc biệt',
  BVMT: 'Thuế bảo vệ môi trường',
  CBPG: 'Thuế chống bán phá giá',
}

interface SearchBarV2Props {
  totalHsCodes: number
  searchQuery: string
}

/** Loại bỏ dấu chấm trong mã HS (vd: "4407.99.90" → "440799990") */
function normalizeQuery(q: string): string {
  // Nếu query toàn là chữ số và dấu chấm → strip dấu chấm
  if (/^[\d.]+$/.test(q.trim())) {
    return q.trim().replace(/\./g, '')
  }
  return q.trim()
}

export default function SearchBarV2({ totalHsCodes, searchQuery }: SearchBarV2Props) {
  const router = useRouter()
  const searchParams = useSearchParams()

  const q = searchParams.get('q')
  const chapterIds = searchParams.get('chapterIds')
  const taxTypes = searchParams.get('taxTypes')

  const [query, setQuery] = useState(searchQuery)
  const [selectedChapters, setSelectedChapters] = useState<number[]>(
    chapterIds ? chapterIds.split(',').map(Number) : [],
  )
  const [selectedTaxTypes, setSelectedTaxTypes] = useState<string[]>(
    taxTypes ? taxTypes.split(',') : [],
  )
  const [showClear, setShowClear] = useState(false)
  const [queryError, setQueryError] = useState('')
  
  // Trạng thái bật tắt dropdown hoàn toàn mới
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const { history, addToHistory, removeFromHistory } = useSearchHistory('hs-search-history')

  const filteredHistory = query.trim()
    ? history.filter((item) => item.toLowerCase().includes(query.toLowerCase()))
    : history

  // Đồng bộ URL parameters vào state
  useEffect(() => {
    setSelectedChapters(chapterIds ? chapterIds.split(',').map(Number) : [])
    setSelectedTaxTypes(taxTypes ? taxTypes.split(',') : [])
    setQuery(q || '')
    setShowClear((q || '').length > 0)
    setQueryError('')
  }, [q, chapterIds, taxTypes])

  // Xử lý Click ra ngoài khối search (bao gồm cả input và dropdown)
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    
    // Dùng mousedown để bắt sớm nhất, ngăn các sự kiện click khác can thiệp
    document.addEventListener('mousedown', handleOutsideClick)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [])

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault()

    if (query.trim() !== '' && query.trim().length < 4) {
      setQueryError('Vui lòng nhập ít nhất 4 ký tự để tìm kiếm')
      return
    }

    setQueryError('')
    const params = new URLSearchParams()

    if (query.trim() !== '') {
      const normalized = normalizeQuery(query)
      addToHistory(query.trim())
      params.set('q', normalized)
    }

    if (selectedChapters.length > 0) {
      params.set('chapterIds', selectedChapters.join(','))
    }

    if (selectedTaxTypes.length > 0) {
      params.set('taxTypes', selectedTaxTypes.join(','))
    }

    setIsDropdownOpen(false)
    const qs = params.toString()
    router.push(qs ? `/ket-qua-tra-cuu-ma-hs?${qs}` : '/ket-qua-tra-cuu-ma-hs')
  }

  const handleClear = () => {
    setQuery('')
    setShowClear(false)
    inputRef.current?.focus()
  }

  const handleSelectHistory = (selectedValue: string) => {
    setQuery(selectedValue)
    setShowClear(true)
    setIsDropdownOpen(false)

    if (selectedValue.trim().length < 3) {
      setQueryError('Vui lòng nhập ít nhất 3 ký tự để tìm kiếm')
      return
    }
    
    setQueryError('')
    addToHistory(selectedValue.trim())
    const params = new URLSearchParams()
    params.set('q', normalizeQuery(selectedValue))
    if (selectedChapters.length > 0) params.set('chapterIds', selectedChapters.join(','))
    if (selectedTaxTypes.length > 0) params.set('taxTypes', selectedTaxTypes.join(','))
    router.push(`/ket-qua-tra-cuu-ma-hs?${params.toString()}`)
  }

  const handleRemoveHistory = (e: React.MouseEvent, term: string) => {
    e.stopPropagation()
    removeFromHistory(term)
  }

  const buildDescription = () => {
    const nodes: React.ReactNode[] = []
    if (q) {
      nodes.push(<span key="q"> cho từ khóa <span className="search-query">&quot;{q}&quot;</span></span>)
    }
    if (chapterIds) {
      const chapters = chapterIds.split(',')
      nodes.push(<span key="ch"> trong <span className="search-query">chương {chapters.join(', ')}</span></span>)
    }
    if (taxTypes) {
      const labels = taxTypes.split(',').map((t) => TAX_LABELS[t] || t).join(', ')
      nodes.push(<span key="tax"> có <span className="search-query">{labels}</span></span>)
    }
    return nodes
  }

  return (
    <div className="cl-card-border mb-6">
      <p className="search-title text-xl" id="search-input">Kết quả tìm kiếm mã HS</p>
      <p className="search-subtitle">
        Tìm thấy <strong>{totalHsCodes} mã HS</strong>
        {buildDescription()}
      </p>

      {/* Search Bar */}
      <div className="mb-4">
        <div className={clsx('relative search-bar-container', queryError && 'mb-1')}>
          {/* Vùng ContainerRef bao trọn cả Input và Dropdown để bắt sự kiện click */}
          <div ref={containerRef} className="search-input-wrapper relative flex-1">
            <Search className="search-icon" size={16} />
            <input
              ref={inputRef}
              type="text"
              name="hs_search_v2_input"
              autoComplete="off"
              spellCheck="false"
              className="search-input-new w-full"
              placeholder="Tìm theo mã HS hoặc tên hàng hóa..."
              value={query}
              onChange={(e) => {
                const val = e.target.value
                setQuery(val)
                setShowClear(val.length > 0)
                setIsDropdownOpen(true)
              }}
              onFocus={() => setIsDropdownOpen(true)}
              onClick={() => setIsDropdownOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setIsDropdownOpen(false)
                  handleSearch()
                } else if (e.key === 'Escape') {
                  handleClear()
                  setIsDropdownOpen(false)
                  inputRef.current?.blur()
                }
              }}
            />
            {showClear && (
              <button 
                type="button" 
                className="search-clear-btn" 
                onClick={handleClear}
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}

            {/* Bảng Dropdown tuỳ chỉnh sạch sẽ không dùng mousedown / stopPropagation */}
            {isDropdownOpen && filteredHistory.length > 0 && (
              <div 
                className="absolute top-[calc(100%+8px)] left-0 z-50 w-full bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden"
              >
                <div className="px-3 py-2 text-xs font-medium text-gray-500 bg-gray-50 border-b border-gray-100">
                  Tìm kiếm gần đây
                </div>
                <ul className="max-h-[300px] overflow-y-auto py-1 m-0 list-none">
                  {filteredHistory.map((term) => (
                    <li
                      key={term}
                      onClick={() => handleSelectHistory(term)}
                      className="flex justify-between items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span>{term}</span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => handleRemoveHistory(e, term)}
                        className="p-1 hover:bg-gray-200 rounded"
                        aria-label="Xoá lịch sử"
                      >
                        <X className="w-3 h-3 text-gray-400" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <button type="button" onClick={handleSearch} className="btn-search-new shrink-0">
            <Search size={16} />
            Tìm kiếm
          </button>
        </div>

        {queryError && <p className="text-red-500 text-xs mt-1">{queryError}</p>}

        <div className="flex items-center gap-3 mt-2 text-[11px] text-[#a3a3a3] select-none">
          <span className="flex items-center gap-1">
            <kbd className="inline-flex items-center px-1.5 py-0.5 rounded border border-[#e5e5e5] bg-[#fafafa] font-mono text-[10px] text-[#737373]">Enter</kbd>
            để tìm kiếm
          </span>
          <span className="text-[#d4d4d4]">·</span>
          <span className="flex items-center gap-1">
            <kbd className="inline-flex items-center px-1.5 py-0.5 rounded border border-[#e5e5e5] bg-[#fafafa] font-mono text-[10px] text-[#737373]">Esc</kbd>
            để đóng / xoá
          </span>
        </div>
      </div>

      {/* Filters Row */}
      <div className="filters-row">
        <div className="filters-left">
          <HSChapterFilter setSelectedChapters={setSelectedChapters} selectedChapters={selectedChapters} />
          <HSTaxFilter setSelectedTaxTypes={setSelectedTaxTypes} selectedTaxTypes={selectedTaxTypes} />
        </div>
      </div>
    </div>
  )
}
